var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'root/src/client/v2/common/components/ui/dialog';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Textarea } from 'root/src/client/v2/common/components/ui/textarea';
import { Checkbox } from 'root/src/client/v2/common/components/ui/checkbox';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { motion, useAnimationControls } from 'framer-motion';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { usePointsEligibility } from 'root/src/client/v2/drop/hooks/usePointsEligibility';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { useDropShortUrl } from 'root/src/client/v2/drop/hooks/useDropShortUrl';
import { RedesignSponsorshipTerms } from 'root/src/client/v2/_pages/drop/redesign/RedesignSponsorshipTerms';
import { sponsorshipRegularTerms } from 'root/src/shared/descriptions/modules/howToEarnPointsModal';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { pointsEndpointIds, STREAM_DATE_IGNORED_CAMPAIGN_IDS } from 'root/src/shared/points/points.constants';
import { MAX_CREATE_POINTS_APPLICATION_LENGTH } from 'root/src/server/modules/points/endpoints/createPointsApplication.endpoint';
import { DateTimePicker } from 'root/src/client/v2/common/components/ui/datetime-picker';
import { FIELD_LABEL_CLASS, streamDateBounds } from 'root/src/client/v2/_pages/drop/redesign/streamDate';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
const SHAKE = { x: [0, -5, 5, -5, 5, 0], transition: { duration: 0.35 } };
export const PointsApplicationDialogContent = ({ drop, open, onOpenChange }) => {
    var _a, _b;
    const { fetcher } = useDareDropApi();
    const utils = trpc.useUtils();
    const shortUrlQuery = useDropShortUrl({ dropId: drop.id });
    const { loading, success, error, run } = useAsyncAction();
    const [comment, setComment] = useState('');
    const [streamDate, setStreamDate] = useState('');
    const [reserveKey, setReserveKey] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [termsError, setTermsError] = useState(false);
    const [dateError, setDateError] = useState(false);
    const termsShake = useAnimationControls();
    const dateShake = useAnimationControls();
    const termsRef = useRef(null);
    const dateRef = useRef(null);
    const isStreamDateVisible = !(drop.isLive || STREAM_DATE_IGNORED_CAMPAIGN_IDS.includes(drop.id));
    const { min: minDate, max: maxDate } = streamDateBounds(drop);
    const streamDateIso = streamDate ? new Date(streamDate).toISOString() : '';
    const apply = () => {
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield fetcher({
                endpointId: pointsEndpointIds.CREATE_POINTS_APPLICATION,
                payload: {
                    dropId: drop.id,
                    comment: comment || undefined,
                    streamDate: isStreamDateVisible && streamDateIso ? streamDateIso : undefined,
                    reserveInitialProduct: reserveKey,
                },
            });
            yield utils.drops.getDrop.invalidate({ mnemonicId: drop.mnemonicId });
        }), {
            onSuccess: () => onOpenChange(false),
            toast: {
                loading: 'Applying for points...',
                success: 'Applied for points. We will review within 48 hours.',
                error: caught => (caught instanceof Error ? caught.message : 'Failed to apply for points'),
            },
        });
    };
    const onSubmit = () => {
        var _a, _b;
        // Keep submit clickable; on invalid, shake + ring the offending field and toast (no layout shift).
        if (!termsAccepted) {
            setTermsError(true);
            (_a = termsRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'center' });
            void termsShake.start(SHAKE);
            toast.error('Please agree to the terms to continue');
            return;
        }
        if (isStreamDateVisible && !streamDate) {
            setDateError(true);
            (_b = dateRef.current) === null || _b === void 0 ? void 0 : _b.scrollIntoView({ behavior: 'smooth', block: 'center' });
            void dateShake.start(SHAKE);
            toast.error('Please add when you plan to stream');
            return;
        }
        if (comment.length > MAX_CREATE_POINTS_APPLICATION_LENGTH) {
            toast.error(`Keep your message under ${MAX_CREATE_POINTS_APPLICATION_LENGTH} characters`);
            return;
        }
        if (!reserveKey && !drop.isGameFree) {
            setConfirming(true);
            return;
        }
        apply();
    };
    return (<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="redesign max-w-lg gap-0 overflow-hidden border-white/[0.08] bg-card/95 p-0 backdrop-blur-xl">
				<DialogHeader className="border-b border-solid border-white/[0.06] px-5 py-4 text-left">
					<DialogTitle className="font-display text-base font-semibold">Apply for Points</DialogTitle>
					<DialogDescription className="text-xs text-muted-foreground">
						We will review your application within 48 hours. You need to be approved to earn points.
					</DialogDescription>
				</DialogHeader>

				<div className="max-h-[60vh] space-y-5 overflow-y-auto px-5 py-4">
					<div>
						<label htmlFor="pa-comment" className={FIELD_LABEL_CLASS}>Why are you a good fit for this drop?</label>
						<Textarea id="pa-comment" rows={3} maxLength={MAX_CREATE_POINTS_APPLICATION_LENGTH} value={comment} onChange={event => setComment(event.target.value)} placeholder="Let the publisher know why you would be a great fit"/>
						<div className="mt-1 text-right text-[11px] text-muted-foreground">{comment.length} / {MAX_CREATE_POINTS_APPLICATION_LENGTH}</div>
					</div>

					{isStreamDateVisible && (<div>
							<label htmlFor="pa-stream-date" className={FIELD_LABEL_CLASS}>Planning to stream at</label>
							<motion.div ref={dateRef} animate={dateShake} className={cn('rounded-lg', dateError && 'ring-2 ring-destructive/40')}>
								<DateTimePicker id="pa-stream-date" value={streamDate} onChange={(value) => { setStreamDate(value); setDateError(false); }} min={minDate} max={maxDate}/>
							</motion.div>
							<p className="mt-1.5 text-[11px] text-muted-foreground">
								Add when you plan to stream so <span className="font-semibold text-foreground">{(_a = drop.brand) === null || _a === void 0 ? void 0 : _a.brandName}</span> can pop in or promote you.
							</p>
						</div>)}

					<div className="rounded-lg border border-solid border-white/[0.06] bg-white/[0.02] p-3">
						<RedesignSponsorshipTerms terms={sponsorshipRegularTerms} dropEvent={drop} shortUrl={(_b = shortUrlQuery.data) !== null && _b !== void 0 ? _b : ''} canEarnPoints isTermsAccepted={termsAccepted} canUserReclaimReservation={false} streamDate={streamDateIso}/>
					</div>

					{!drop.isGameFree && (<label htmlFor="pa-reserve" className="flex cursor-pointer select-none items-center gap-2 text-sm text-foreground">
							<Checkbox id="pa-reserve" checked={reserveKey} onCheckedChange={setReserveKey}/>
							I would need the game key.
						</label>)}

					<motion.label ref={termsRef} htmlFor="pa-terms" animate={termsShake} className="flex cursor-pointer select-none items-start gap-2 rounded-lg text-sm text-foreground">
						<Checkbox id="pa-terms" checked={termsAccepted} onCheckedChange={(value) => { setTermsAccepted(value); setTermsError(false); }} className={termsError ? 'border-destructive ring-2 ring-destructive/40' : undefined}/>
						<span>I agree to the terms for earning Points on this Drop. <span className="text-neon-pink">*</span></span>
					</motion.label>
				</div>

				<div className="border-t border-solid border-white/[0.06] px-5 py-4">
					{confirming ? (<div className="space-y-3">
							<p className="text-sm text-muted-foreground">
								Apply without reserving a game key? Keys may run out before you can claim one.
								Ignore this if you already have a key or will not need one.
							</p>
							<div className="flex gap-2">
								<Button variant="secondary" onClick={() => setConfirming(false)} className="flex-1 rounded-lg text-sm">
									Go back
								</Button>
								<Button variant="brand" loading={loading} success={success} error={error} onClick={() => { setConfirming(false); apply(); }} className="flex-1 rounded-lg text-sm">
									Apply anyway
								</Button>
							</div>
						</div>) : (<Button variant="brand" loading={loading} success={success} error={error} onClick={onSubmit} className="w-full rounded-lg text-sm font-semibold">
							Apply for Points
						</Button>)}
				</div>
			</DialogContent>
		</Dialog>);
};
export const PointsApplicationDialog = ({ drop }) => {
    var _a, _b;
    const isAuthenticated = useIsAuthenticated();
    const [open, setOpen] = useState(false);
    // Mirror the legacy gate (HeroSection): only a Twitch Partner with CCV >= 50 (bypassed in dev),
    // on an eligible, non-blocklisted points drop, can apply for points. No admin bypass here: the
    // admin sees the meter (usePointsEligibility) but applying stays creator-only.
    const { canEarnPoints } = usePointsEligibility(drop);
    const pointsLeft = ((_a = drop.pointsLimit) !== null && _a !== void 0 ? _a : 0) - ((_b = drop.pointsAwarded) !== null && _b !== void 0 ? _b : 0);
    const eligible = isAuthenticated
        && canEarnPoints
        && drop.awardPoints
        && Boolean(drop.pointsApplicationsEnabled)
        && !drop.pointsApplicationState
        && pointsLeft > 0
        && !drop.isCreatorPointsAllowlisted
        && drop.isAbleToParticipate;
    if (!eligible) {
        return null;
    }
    return (<>
			<Button variant="brand" onClick={() => setOpen(true)} className="mt-5 w-full rounded-lg text-xs font-semibold">
				Apply for Points
			</Button>
			<PointsApplicationDialogContent drop={drop} open={open} onOpenChange={setOpen}/>
		</>);
};
